// =====================================================
// 📁 POSCreditNoteProductCard.tsx
// =====================================================

import { useEffect, useState } from 'react';

import { motion } from 'framer-motion';

import POSCreditNoteOverlay from '../shared/POSCreditNoteOverlay';

import POSActionButton from '../shared/POSActionButton';

import ProductPanelService from '../../../../../services/product/ProductPanelService';

import POSCreditQuantityModal from '../modal/POSCreditQuantityModal';

import { useSaleDispatcher } from '../../../../../hooks/sale/useSaleDispatcher';

import { useSale } from '../../../../../hooks/sale/useSale';

interface Props {
  product: any;
}

const POSCreditNoteProductCard: React.FC<Props> = ({ product }) => {
  // =====================================================
  // 🔹 Hooks
  // =====================================================
  const { addProduct } = useSaleDispatcher();

  const { form } = useSale();

  // =====================================================
  // 🔹 States
  // =====================================================
  const [showModal, setShowModal] = useState(false);

  // =====================================================
  // 🔹 States
  // =====================================================
  const [imageUrl, setImageUrl] = useState('');

  // =====================================================
  // 🔹 Effects
  // =====================================================
  useEffect(() => {
    const loadImage = async () => {
      if (!product.imagePath) return;

      try {
        const url = await ProductPanelService.getProductImage(
          product.imagePath
        );

        if (url) {
          setImageUrl(url);
        }
      } catch (error) {
        console.error('❌ Error loading product image:', error);
      }
    };

    loadImage();
  }, [product.imagePath]);

  const soldQuantity = Number(product.soldQuantity ?? product.quantity ?? 0);

  const returnedQuantity = Number(product.returnedQuantity ?? 0);

  const remainingQuantity = Number(
    product.remainingQuantity ?? soldQuantity - returnedQuantity
  );

  const canBeReturned =
    product.canBeReturned !== false && remainingQuantity > 0;

  // =====================================================
  // 🔹 Confirm Return
  // =====================================================
  const handleConfirmReturn = (quantity: number) => {
    if (!form?.id) return;

    addProduct({
      saleId: form.id,

      productId: product.productId,

      quantity,

      unitPrice: Number(product.unitPrice || 0),

      taxType: 'TAXED',

      creditFiscalId: product.creditFiscalId,
    });

    setShowModal(false);
  };

  // =====================================================
  // 🎨 Render
  // =====================================================
  return (
    <>
      <motion.div
        layout
        whileHover={{ y: -4 }}
        transition={{ duration: 0.2 }}
        className="
          relative
          flex
          flex-col
          overflow-hidden
          rounded-2xl
          border
          border-stroke
          bg-white
          shadow-sm
          transition-all
          dark:border-strokedark
          dark:bg-boxdark
        "
      >
        {/* 🔹 Overlay */}
        {!canBeReturned && <POSCreditNoteOverlay />}

        {/* 🔹 Image */}
        <div
          className="
            bg-gray-1
            relative
            flex
            h-[180px]
            items-center
            justify-center
            overflow-hidden
            border-b
            border-stroke
            dark:border-strokedark
            dark:bg-meta-4
          "
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={product.productName}
              className="
                h-full
                w-full
                object-cover
              "
            />
          ) : (
            <span
              className="
                text-sm
                font-medium
                text-body
              "
            >
              IMG
            </span>
          )}

          {/* 🔹 Available Badge */}
          <div
            className="
              absolute
              right-3
              top-3
              rounded-full
              bg-white/95
              px-3
              py-1
              text-xs
              font-semibold
              text-[#3CA745]
              shadow-sm
              dark:bg-boxdark/95
            "
          >
            {remainingQuantity} disponibles
          </div>
        </div>

        {/* 🔹 Content */}
        <div className="flex flex-1 flex-col p-4">
          {/* 🔹 Name */}
          <h3
            className="
              line-clamp-2
              min-h-[48px]
              text-base
              font-semibold
              text-black
              dark:text-white
            "
          >
            {product.productName}
          </h3>

          {/* 🔹 Price */}
          <div
            className="
              mt-3
              text-2xl
              font-bold
              text-primary
            "
          >
            ${Number(product.unitPrice || 0).toFixed(2)}
          </div>

          {/* 🔹 Stats */}
          <div
            className="
              mt-4
              flex
              flex-wrap
              gap-2
            "
          >
            <div
              className="
                rounded-full
                bg-primary/10
                px-3
                py-1
                text-[11px]
                font-semibold
                text-primary
              "
            >
              Vendidos: {soldQuantity}
            </div>

            <div
              className="
                rounded-full
                bg-[#DC3545]/10
                px-3
                py-1
                text-[11px]
                font-semibold
                text-[#DC3545]
              "
            >
              Devueltos: {returnedQuantity}
            </div>

            <div
              className="
                rounded-full
                bg-[#3CA745]/10
                px-3
                py-1
                text-[11px]
                font-semibold
                text-[#3CA745]
              "
            >
              Restantes: {remainingQuantity}
            </div>
          </div>

          {/* 🔹 Action */}
          <div className="mt-auto pt-5">
            <POSActionButton
              fullWidth
              variant="primary"
              disabled={!canBeReturned}
              onClick={() => setShowModal(true)}
            >
              Agregar devolución
            </POSActionButton>
          </div>
        </div>
      </motion.div>

      {/* 🔹 Modal */}
      <POSCreditQuantityModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onConfirm={handleConfirmReturn}
        product={{
          productId: product.productId,

          productName: product.productName,

          quantity: soldQuantity,

          unitPrice: Number(product.unitPrice || 0),

          returnedQuantity,
        }}
      />
    </>
  );
};

export default POSCreditNoteProductCard;
